import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useAppDispatch } from '../../store/hook';
import { requestPiiAccess } from '../../features/voucher/VoucherSlice';

interface RequestPiiAccessModalProps {
  show: boolean;
  onClose: () => void;
  entityName: string;
  entityId: number;
  onSuccess: (pii: Record<string, unknown>) => void;
}

const RequestPiiAccessModal: React.FC<RequestPiiAccessModalProps> = ({
  show,
  onClose,
  entityName,
  entityId,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = reason.trim();
    if (!trimmed) {
      setError('Reason for access is required and cannot be empty.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await dispatch(
        requestPiiAccess({
          entity_name: entityName,
          entity_id: entityId,
          request_context: trimmed,
          // Only request minimal required PII fields with receiver fallbacks
          fields: ["beneficiaryName", "beneficiaryPhone", "receiverName", "receiverPhone"],
        })
      ).unwrap();
      setReason('');
      onSuccess(result.pii);
      onClose();
    } catch (err: unknown) {
      setError(typeof err === 'string' ? err : 'Failed to access PII. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setReason('');
      setError(null);
      onClose();
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Request PII Access</h3>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <label htmlFor="request-context" className="block text-sm font-medium text-gray-700 mb-2">
            Reason for access <span className="text-red-500">*</span>
          </label>
          <textarea
            id="request-context"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError(null);
            }}
            placeholder="e.g. Customer support request, dispute resolution"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={loading}
            required
          />
          {error && (
            <div className="mt-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-200">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-3 mt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !reason.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Request Access
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestPiiAccessModal;
